import React, { useState, useEffect, useContext } from 'react';
import Modal from '../../common/Modal';
import { OpaqueSpinner } from '../../common/LoadSpinner';
import { batchUpdate, textBatchUpdate } from '../constants/handleResponseData';
import { QueryContext } from '../../../hooks/Contexts';
import { confirmation } from '../../../helpers/notifications';
import OptionBatch from './OptionBatch';
import FindAndReplace from './FindAndReplace';

const defaultTextState = {
  old_txt: '',
  new_txt: '',
  remove_whitespace: '',
  prepend_txt: '',
  append_txt: '',
};

const BatchUpdateModal = ({
  viewBatchUpdate,
  setViewBatchUpdate,
  fieldsets,
  submissionCount,
}) => {
  const { queryParams, setQueryParams } = useContext(QueryContext);
  const [updateType, setUpdateType] = useState('option');
  const [selectedField, setSelectedField] = useState(null);
  // Option State
  const [selectedOpt, setSelectedOpt] = useState(null);
  // FindAndReplace State
  const [textState, setTextState] = useState(defaultTextState);

  const resetSelected = () => {
    setSelectedOpt(null);
    setSelectedField(null);
    setTextState(defaultTextState);
  };

  const toggleUpdateType = (type) => {
    resetSelected();
    setUpdateType(type);
  };

  const closeModal = () => {
    resetSelected();
    setViewBatchUpdate(false);
  };

  const handleCleanup = () => {
    setQueryParams({ ...queryParams });
    closeModal();
  };

  const determineField = (fieldId) => {
    let fields = fieldsets.map((fs) => fs.fields).flat();
    setSelectedField(fields.find((f) => f.id == fieldId));
    setSelectedOpt(null);
  };

  const determineOpt = (optId, field) => {
    if (optId === 'destroy') {
      setSelectedOpt('destroy');
    } else {
      setSelectedOpt(field ? field.options.find((o) => o.id == optId) : null);
    }
  };

  const urlBatchParamsBuilder = (type) => {
    let params = {
      form_id: fieldsets[0].form_id,
      field_id: selectedField.id,
      filters: JSON.stringify(queryParams.filters),
    };
    if (type === 'option') {
      params.field_option_id = selectedOpt?.id || null;
    } else if (type === 'text') {
      params = { ...params, ...textState };
    }
    let urlParams = new URLSearchParams(params).toString();
    return urlParams;
  };

  const handleClearAllOpt = async (e) => {
    e.target.blur();
    confirmation.acknowledge(
      async function confirmed() {
        let params = urlBatchParamsBuilder('option') + '&delete_all=true';
        let result = await batchUpdate(params);
        if (result.ok) handleCleanup();
      },
      function cancelled() {},
      'Confirm Update',
      `Potentially updating ${submissionCount} submissions, are you sure?`,
      'Confirm',
      'Cancel'
    );
  };

  const handleUpdateAllOpt = async (e) => {
    e.target.blur();
    confirmation.acknowledge(
      async function confirmed() {
        let params = urlBatchParamsBuilder('option');
        let result = await batchUpdate(params);
        if (result.ok) handleCleanup();
      },
      function cancelled() {},
      'Confirm Update',
      `Potentially updating ${submissionCount} submissions, are you sure?`,
      'Confirm',
      'Cancel'
    );
  };

  const handleClearText = async (e) => {
    e.target.blur();
    confirmation.acknowledge(
      async function confirmed() {
        let params = urlBatchParamsBuilder('text') + '&clear_values=true';
        let result = await textBatchUpdate(params);
        if (result.ok) handleCleanup();
      },
      function cancelled() {},
      'Confirm Update',
      `Potentially updating ${submissionCount} submissions, are you sure?`,
      'Confirm',
      'Cancel'
    );
  };

  const handleUpdateText = async (e) => {
    e.target.blur();
    confirmation.acknowledge(
      async function confirmed() {
        let params = urlBatchParamsBuilder('text');
        let result = await textBatchUpdate(params);
        if (result.ok) handleCleanup();
      },
      function cancelled() {},
      'Confirm Update',
      `Potentially updating ${submissionCount} submissions, are you sure?`,
      'Confirm',
      'Cancel'
    );
  };

  useEffect(() => {
    if (!selectedField) setSelectedOpt(null);
  }, [selectedField]);

  return (
    <Modal
      show={viewBatchUpdate}
      onClose={closeModal}
      title='submission-batch-update'
    >
      <header>
        <h3>Batch Update</h3>
      </header>

      <div className='batch-select'>
        <button
          onClick={() => toggleUpdateType('option')}
          className={updateType == 'option' ? 'blue selected' : 'blue'}
        >
          Update Option Field
        </button>

        <button
          onClick={() => toggleUpdateType('text')}
          className={updateType == 'text' ? 'blue selected' : 'blue'}
        >
          Update Text Field
        </button>
      </div>

      {updateType === 'option' ? (
        <OptionBatch
          fieldsets={fieldsets}
          determineField={determineField}
          determineOpt={determineOpt}
          handleClearAll={handleClearAllOpt}
          handleUpdateAll={handleUpdateAllOpt}
          selectedField={selectedField}
          selectedOpt={selectedOpt}
          submissionCount={submissionCount}
        />
      ) : (
        <FindAndReplace
          fieldsets={fieldsets}
          determineField={determineField}
          selectedField={selectedField}
          submissionCount={submissionCount}
          textState={textState}
          setTextState={setTextState}
          handleClearText={handleClearText}
          handleUpdateText={handleUpdateText}
        />
      )}

      <OpaqueSpinner />
    </Modal>
  );
};

export default BatchUpdateModal;
